import { useMemo, memo } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { SessionTrendDataDto } from '@repo/shared/dtos/analytics/AnalyticsResponseDto';
import { CHART_THEMES, CHART_COLORS, CHART_MARGINS } from '../config';
import { formatNumber } from '../utils/dataUtils';
import {
  downsampleData,
  shouldDownsample,
  getOptimalMaxPoints,
} from '../../../utils/chartDownsampling';

interface SessionTrendsChartProps {
  data: SessionTrendDataDto[];
  height?: number;
  showSessions?: boolean;
  showMinutes?: boolean;
}

export const SessionTrendsChart = memo(function SessionTrendsChart({
  data,
  height = 280,
  showSessions = true,
  showMinutes = false,
}: SessionTrendsChartProps) {
  const normalizedData = useMemo(() => {
    if (!data?.length) {
      return [] as SessionTrendDataDto[];
    }

    const parsed = data
      .map(item => {
        const date = new Date(item.monthStart);
        const time = date.getTime();

        if (Number.isNaN(time)) {
          return null;
        }

        return {
          key: date.toISOString().slice(0, 10),
          time,
          date,
          item,
          isLikelyDaily: date.getUTCDate() !== 1,
        };
      })
      .filter(Boolean) as Array<{
      key: string;
      time: number;
      date: Date;
      item: SessionTrendDataDto;
      isLikelyDaily: boolean;
    }>;

    if (!parsed.length) {
      return [] as SessionTrendDataDto[];
    }

    parsed.sort((a, b) => a.time - b.time);

    const byDate = new Map<string, SessionTrendDataDto>();

    parsed.forEach(entry => {
      const existing = byDate.get(entry.key);

      if (!existing) {
        byDate.set(entry.key, entry.item);
        return;
      }

      const existingDate = new Date(existing.monthStart);
      const existingIsDaily = existingDate.getUTCDate() !== 1;

      if (!existingIsDaily && entry.isLikelyDaily) {
        byDate.set(entry.key, entry.item);
        return;
      }

      if (existingIsDaily === entry.isLikelyDaily) {
        byDate.set(entry.key, entry.item);
      }
    });

    const dedupedData = Array.from(byDate.entries())
      .sort((a, b) => new Date(a[1].monthStart).getTime() - new Date(b[1].monthStart).getTime())
      .map(entry => entry[1]);

    // Apply downsampling if data is too large
    if (shouldDownsample(dedupedData.length)) {
      const maxPoints = getOptimalMaxPoints(dedupedData.length);
      return downsampleData(dedupedData, maxPoints, item => item.sessionCount);
    }

    return dedupedData;
  }, [data]);

  if (!normalizedData.length) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500" style={{ height }}>
        <p className="text-sm">No trend data available</p>
      </div>
    );
  }

  const isDaily = normalizedData.some(item => {
    const d = new Date(item.monthStart);
    return d.getUTCDate() !== 1;
  });

  const formatX = (d: Date | string): string => {
    const date = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(date.getTime())) return 'Invalid Date';
    if (isDaily) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  const lineData: Array<{ id: string; color: string; data: Array<{ x: string; y: number }> }> = [];

  if (showSessions) {
    lineData.push({
      id: 'sessions',
      color: CHART_COLORS[0],
      data: normalizedData.map(item => ({
        x: formatX(item.monthStart),
        y: item.sessionCount,
      })),
    });
  }

  if (showMinutes) {
    lineData.push({
      id: 'minutes',
      color: CHART_COLORS[1] || CHART_COLORS[0],
      data: normalizedData.map(item => ({
        x: formatX(item.monthStart),
        y: Math.round(item.totalMinutes),
      })),
    });
  }

  const allX = lineData[0]?.data.map(d => d.x) || [];

  const generateDailyTicks = (dates: string[]): string[] | undefined => {
    if (!isDaily) return undefined;

    const numDays = dates.length;

    if (numDays <= 7) return undefined;

    if (numDays <= 14) {
      return dates.filter((_, idx) => idx % 2 === 0);
    }

    if (numDays <= 30) {
      return dates.filter((_, idx) => idx % 3 === 0);
    }

    if (numDays <= 60) {
      return dates.filter((_, idx) => idx % 5 === 0);
    }

    if (numDays <= 90) {
      return dates.filter((_, idx) => idx % 7 === 0);
    }

    return dates.filter((_, idx) => idx % 10 === 0);
  };

  const generateMonthlyTicks = (dates: string[]): string[] | undefined => {
    if (isDaily) return undefined;

    return dates;
  };

  const tickValues = isDaily ? generateDailyTicks(allX) : generateMonthlyTicks(allX);

  return (
    <div style={{ height }}>
      <ResponsiveLine
        data={lineData.map(series => ({ id: series.id, data: series.data }))}
        margin={CHART_MARGINS.default}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 0, max: 'auto' }}
        curve="linear"
        axisTop={null}
        axisRight={null}
        axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: -45, tickValues }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: showSessions && showMinutes ? 'Count' : showSessions ? 'Sessions' : 'Minutes',
          legendOffset: -40,
          legendPosition: 'middle',
        }}
        pointSize={6}
        pointColor={{ theme: 'background' }}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        pointLabelYOffset={-12}
        useMesh={true}
        colors={lineData.map(series => series.color)}
        enableGridX={false}
        enableGridY={true}
        animate={true}
        motionConfig="gentle"
        lineWidth={3}
        legends={[
          {
            anchor: 'bottom-right',
            direction: 'column',
            justify: false,
            translateX: 100,
            translateY: 0,
            itemsSpacing: 0,
            itemDirection: 'left-to-right',
            itemWidth: 80,
            itemHeight: 20,
            itemOpacity: 0.75,
            symbolSize: 12,
            symbolShape: 'circle',
            symbolBorderColor: '#fff',
            effects: [
              {
                on: 'hover',
                style: { itemBackground: '#fff', itemOpacity: 1 },
              },
            ],
          },
        ]}
        tooltip={({ point }: any) => (
          <div className="rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] px-3 py-2 text-[var(--anl-text)]">
            <div className="text-xs text-[var(--anl-text-muted)]">{point.data.xFormatted}</div>
            <div className="mt-1 flex items-center gap-2">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: point.serieColor }}
              />
              <span className="text-xs capitalize text-[var(--anl-text-muted)]">
                {point.serieId}
              </span>
              <span className="text-sm font-semibold">{formatNumber(point.data.yFormatted)}</span>
            </div>
          </div>
        )}
        theme={CHART_THEMES.default}
      />
    </div>
  );
});
